'use client'

import { useRef, useEffect, useState } from 'react'
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'motion/react'
import Image from 'next/image'
import { fetchGitHubProjectVersions, type GitHubProjectVersion } from '../utils/github'

type ProjectDataItem = {
	title: string
	value: string
	link?: string
}

const projects = [
	{
		id: 1,
		title: 'My Alias',
		description: 'Email alias management for privacy-focused users.',
		image: '/imgs/projects/myalias.png',
		tags: ['Backend', 'Email', 'Privacy'],
		link: 'https://myalias.pro/',
		data: [
			{ title: 'Version', value: '3.0.0' },
			{ title: 'Status', value: 'Production' },
			{ title: 'Stack', value: 'Next.js' }
		]
	},
	{
		id: 2,
		title: 'TrefortBank',
		description: 'Banking and financial web application.',
		image: '/imgs/projects/trefortbank.png',
		tags: ['Web', 'Finance', 'Next.js'],
		link: 'https://bank.trefort.eu/',
		data: [
			{ title: 'Status', value: 'Production' },
			{ title: 'Stack', value: 'Next.js, TypeScript' }
		]
	},
	{
		id: 3,
		title: 'BME MTMT Scoring',
		description: 'Scoring system for academic publications.',
		image: '/imgs/projects/mtmtscoring.png',
		tags: ['Academic', 'Backend'],
		link: 'https://mtmtscoring.bme.hu',
		data: [
			{ title: 'Version', value: '1.7.1' },
			{ title: 'Collaboration', value: ' BME University', link: 'https://kozlekedes.bme.hu/' },
			{ title: 'Stack', value: 'TypeScript, Next.js' }
		]
	},
	{
		id: 4,
		title: 'NULNET',
		description: 'Linux host agent: telemetry, Unix-socket JSON-RPC, self-updates.',
		image: '/imgs/projects/nulnet.svg',
		tags: ['Rust', 'Telemetry', 'Linux'],
		link: 'https://github.com/Da4ndo/nulnet',
		githubRepo: { owner: 'Da4ndo', repo: 'nulnet', fallbackVersion: 'v1.3.4' },
		data: [
			{ title: 'Version', value: 'Loading...' },
			{ title: 'Status', value: 'Open Source' },
			{ title: 'Stack', value: 'Rust, Tokio' }
		]
	},
	{
		id: 5,
		title: 'NULCTL',
		description: 'Operator CLI for NULNET with a live TUI telemetry dashboard.',
		image: '/imgs/projects/nulctl.png',
		tags: ['Rust', 'CLI', 'TUI'],
		link: 'https://github.com/Da4ndo/nulctl',
		githubRepo: { owner: 'Da4ndo', repo: 'nulctl', fallbackVersion: 'v0.5.4' },
		data: [
			{ title: 'Version', value: 'Loading...' },
			{ title: 'Status', value: 'Open Source' },
			{ title: 'Stack', value: 'Rust, Ratatui' }
		]
	},
	{
		id: 6,
		title: 'BME MTMT Statistics',
		description: 'Analytics platform for MTMT publications.',
		image: '/imgs/projects/mtmtstatistics2.png',
		tags: ['Data Analysis', 'Visualization'],
		link: 'https://mtmtstatistics.bme.hu',
		data: [
			{ title: 'Version', value: '1.7.4' },
			{ title: 'Collaboration', value: ' BME University', link: 'https://kozlekedes.bme.hu/' },
			{ title: 'Stack', value: 'Next.js, D3.js' }
		]
	},
	{
		id: 7,
		title: 'Hyde-Ext',
		description: 'Rust CLI tool for HyDE environment automation.',
		image: '/imgs/projects/hyde-ext.png',
		tags: ['CLI', 'Rust'],
		link: 'https://github.com/Da4ndo/Hyde-Ext',
		githubRepo: { owner: 'Da4ndo', repo: 'Hyde-Ext', fallbackVersion: 'v1.1.1' },
		data: [
			{ title: 'Version', value: 'Loading...' },
			{ title: 'Status', value: 'Open Source' },
			{ title: 'Stack', value: 'Rust' }
		]
	},
	{
		id: 8,
		title: 'CEngine',
		description: 'Python to executable converter in Rust.',
		image: '/imgs/projects/cengine.png',
		tags: ['Rust', 'CLI', 'Python'],
		link: 'https://github.com/Da4ndo/CEngine',
		githubRepo: { owner: 'Da4ndo', repo: 'CEngine', fallbackVersion: 'v1.2.0' },
		data: [
			{ title: 'Version', value: 'v1.2.0' },
			{ title: 'Stack', value: 'Rust' }
		]
	},
	{
		id: 9,
		title: 'Project Cleaner',
		description: 'Fast build artifacts cleaner in Rust.',
		image: '/imgs/projects/project-cleaner.svg',
		tags: ['Rust', 'CLI', 'DevTools'],
		link: 'https://github.com/Da4ndo/project-cleaner',
		githubRepo: { owner: 'Da4ndo', repo: 'project-cleaner', fallbackVersion: 'v2.0.0' },
		data: [
			{ title: 'Version', value: 'v2.0.0' },
			{ title: 'Stack', value: 'Rust' }
		]
	},
	{
		id: 10,
		title: 'ProjectStructure',
		description: 'Project structure analyzer and preserver.',
		image: '/imgs/projects/project-structure.svg',
		tags: ['Python', 'CLI'],
		link: 'https://github.com/Da4ndo/projectstructure',
		githubRepo: { owner: 'Da4ndo', repo: 'projectstructure', fallbackVersion: '0.1.15' },
		data: [
			{ title: 'Version', value: 'Loading...' },
			{ title: 'Stack', value: 'Python' }
		]
	},
	{
		id: 11,
		title: 'socket-api2',
		description: 'Python package for server-client connections.',
		image: '/imgs/projects/socket-api2.svg',
		tags: ['Python', 'Networking'],
		link: 'https://pypi.org/project/socket-api2/',
		data: [
			{ title: 'Version', value: '0.2.0' },
			{ title: 'Stack', value: 'Python' }
		]
	},
	{
		id: 12,
		title: 'Magyar Szív',
		description: 'Community platform for Hungarian culture and heritage.',
		image: '/imgs/projects/magyarsziv.png',
		tags: ['Community', 'Culture'],
		unavailableMessage: 'The live site is no longer online.',
		data: [
			{ title: 'Status', value: 'Sunset' },
			{ title: 'Collaboration', value: ' Scruton', link: 'https://scruton.hu' },
			{ title: 'Stack', value: 'Next.js, TypeScript' }
		]
	}
]

type BentoPosition = 'left' | 'right' | 'middle' | 'full'

const ROUNDED_BY_POSITION: Record<BentoPosition, string> = {
	left: 'rounded-tl-3xl rounded-bl-3xl rounded-tr-sm rounded-br-sm',
	right: 'rounded-tr-3xl rounded-br-3xl rounded-tl-sm rounded-bl-sm',
	middle: 'rounded-sm',
	full: 'rounded-3xl'
}

interface ProjectCardProps {
	project: (typeof projects)[number]
	uniqueKey: string
	position?: BentoPosition
}

function ProjectCard({ project, uniqueKey, position = 'middle' }: ProjectCardProps) {
	const roundedClass = ROUNDED_BY_POSITION[position]
	const cardRef = useRef<HTMLElement>(null)
	const x = useMotionValue(0)
	const y = useMotionValue(0)
	const mouseXSpring = useSpring(x, { stiffness: 300, damping: 30 })
	const mouseYSpring = useSpring(y, { stiffness: 300, damping: 30 })
	const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['5deg', '-5deg'])
	const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-5deg', '5deg'])
	const hasLiveLink = Boolean(project.link)

	const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
		if (!hasLiveLink || !cardRef.current) return
		const rect = cardRef.current.getBoundingClientRect()
		const centerX = rect.left + rect.width / 2
		const centerY = rect.top + rect.height / 2
		x.set((e.clientX - centerX) / rect.width)
		y.set((e.clientY - centerY) / rect.height)
	}

	const handleMouseLeave = () => {
		x.set(0)
		y.set(0)
	}

	const cardClassName =
		`glass-card ${roundedClass} overflow-hidden group block relative w-full h-full ` +
		`min-h-[200px] z-0 hover:z-10 origin-center ` +
		(hasLiveLink ? 'cursor-pointer' : 'cursor-default')

	const motionShellProps = {
		className: cardClassName,
		style: {
			rotateX,
			rotateY,
			transformStyle: 'preserve-3d' as const,
			perspective: '1000px'
		},
		onMouseMove: handleMouseMove,
		onMouseLeave: handleMouseLeave,
		whileHover: hasLiveLink ? { scale: 1.03 } : {}
	}

	const offlineNote =
		project.unavailableMessage ?? 'Live site unavailable.'

	const cardBody = (
		<>
			<motion.div
				className={`absolute inset-0 ${roundedClass} opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10`}
				style={{
					background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.05))',
					filter: 'blur(20px)'
				}}
			/>

			<div className="absolute inset-0 overflow-hidden">
				<motion.div
					className="absolute inset-0"
					whileHover={hasLiveLink ? { scale: 1.08 } : {}}
					transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
				>
					<Image
						src={project.image}
						alt={project.title}
						fill
						className="object-cover"
						sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
					/>
				</motion.div>

				{!hasLiveLink && (
					<div
						className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium text-amber-200 bg-black/55 backdrop-blur-md border border-amber-400/35"
						role="status"
					>
						Site offline
					</div>
				)}

				<div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/70 via-black/40 to-transparent px-3 py-2.5 md:px-4 md:py-3">
					<span className="text-xs md:text-sm lg:text-base font-medium text-white/95 line-clamp-2 wrap-break-word">
						{project.title}
					</span>
				</div>

				<div className="absolute inset-0 bg-black/75 dark:bg-black/80 flex flex-col justify-end p-3 md:p-4 lg:p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
					<h3 className="text-base md:text-lg lg:text-xl font-bold text-white mb-1.5 group-hover:text-green-400 transition-colors">
						{project.title}
					</h3>
					<p className="text-xs md:text-sm text-white/80 mb-2 line-clamp-2">
						{project.description}
					</p>
					<div className="space-y-0.5 mb-2 text-xs md:text-sm text-white/70">
						{project.data.slice(0, 2).map((item: ProjectDataItem, idx) => (
							<div key={idx} className="flex gap-2">
								<span className="text-white/50 shrink-0">{item.title}:</span>
								<span className="truncate">
									{item.link ? (
										<span className="text-green-400">{item.value}</span>
									) : item.value}
								</span>
							</div>
						))}
					</div>
					<div className="flex flex-wrap gap-1 mb-3">
						{project.tags.slice(0, 3).map(tag => (
							<span
								key={`${uniqueKey}-${tag}`}
								className="px-2 py-0.5 bg-white/15 text-white/90 rounded-full text-xs md:text-sm"
							>
								{tag}
							</span>
						))}
					</div>
					{hasLiveLink ? (
						<div className="flex items-center gap-1.5 text-green-400 text-xs md:text-sm font-medium">
							<span>View</span>
							<motion.svg
								className="w-3.5 h-3.5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								animate={{ x: [0, 3, 0] }}
								transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
							>
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
							</motion.svg>
						</div>
					) : (
						<p className="flex items-start gap-2 text-amber-300/95 text-xs md:text-sm leading-snug">
							<span
								className="mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-400"
								aria-hidden
							/>
							<span>{offlineNote}</span>
						</p>
					)}
				</div>
			</div>
		</>
	)

	return hasLiveLink ? (
		<motion.a
			{...motionShellProps}
			ref={cardRef as React.RefObject<HTMLAnchorElement>}
			href={project.link}
			target="_blank"
			rel="noopener noreferrer"
		>
			{cardBody}
		</motion.a>
	) : (
		<motion.div
			{...motionShellProps}
			ref={cardRef as React.RefObject<HTMLDivElement>}
			role="group"
			aria-label={`${project.title}: ${offlineNote}`}
		>
			{cardBody}
		</motion.div>
	)
}

const STAGGER_DELAY = 0.08
const STAGGER_DURATION = 0.5

const Work = () => {
	const ref = useRef(null)
	const isInView = useInView(ref, { once: false, amount: 0.1 })
	const [projectVersions, setProjectVersions] = useState<GitHubProjectVersion[]>([])

	useEffect(() => {
		const fetchVersions = async () => {
			const githubProjects = projects
				.filter(project => project.githubRepo)
				.map(project => project.githubRepo!)
			const versions = await fetchGitHubProjectVersions(githubProjects)
			setProjectVersions(versions)
		}
		fetchVersions()
	}, [])

	const updatedProjects = projects.map(project => {
		if (!project.githubRepo) return project
		const versionInfo = projectVersions.find(
			v => v.owner === project.githubRepo!.owner && v.repo === project.githubRepo!.repo
		)
		if (!versionInfo) return project
		return {
			...project,
			data: project.data.map(item => {
				if (!item.title.toLowerCase().includes('version')) return item
				return { ...item, value: versionInfo.parsedVersion ?? 'N/A' }
			})
		}
	})

	return (
		<section id="work" className="py-24 md:py-32">
			<div className="section-container" ref={ref}>
				<div className="space-y-16">
					{/* Header */}
					<motion.div
						className="text-center max-w-2xl mx-auto"
						initial={{ opacity: 0, y: 40 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
					>
						<h2 className="text-3xl md:text-5xl font-bold mb-6">
							Featured <span className="text-gradient">Projects</span>
						</h2>
						<p className="text-lg text-gray-600 dark:text-gray-400">
							A selection of my recent projects and collaborations.
						</p>
					</motion.div>
				</div>

				{/* Organic bento: 12-col grid, position-based corner rounding */}
				<div
					className="mt-16 grid grid-cols-4 md:grid-cols-12 gap-2 md:gap-3 md:grid-rows-[repeat(8,minmax(100px,1fr))]"
				>
						{/* My Alias - MAIN - 7 cols, 2 rows */}
						<motion.div
							key={updatedProjects[0].id}
							className="col-span-4 md:col-span-7 md:row-span-2 min-h-[250px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 0 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[0]}
								uniqueKey="0"
								position="left"
							/>
						</motion.div>

						{/* TrefortBank - MAIN - 5 cols, 2 rows */}
						<motion.div
							key={updatedProjects[1].id}
							className="col-span-4 md:col-span-5 md:row-span-2 min-h-[250px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 1 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[1]}
								uniqueKey="1"
								position="right"
							/>
						</motion.div>

						{/* Hyde-Ext - side - 4 cols */}
						<motion.div
							key={updatedProjects[6].id}
							className="col-span-2 md:col-span-4"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 2 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[6]}
								uniqueKey="6"
								position="left"
							/>
						</motion.div>

						{/* BME MTMT Scoring - MAIN - 8 cols */}
						<motion.div
							key={updatedProjects[2].id}
							className="col-span-2 md:col-span-8"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 3 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[2]}
								uniqueKey="2"
								position="right"
							/>
						</motion.div>

						{/* NULNET - MAIN - 6 cols, 2 rows */}
						<motion.div
							key={updatedProjects[3].id}
							className="col-span-4 md:col-span-6 md:row-span-2 min-h-[250px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 4 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[3]}
								uniqueKey="3"
								position="left"
							/>
						</motion.div>

						{/* NULCTL - MAIN - 6 cols, 2 rows */}
						<motion.div
							key={updatedProjects[4].id}
							className="col-span-4 md:col-span-6 md:row-span-2 min-h-[250px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 5 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[4]}
								uniqueKey="4"
								position="right"
							/>
						</motion.div>

						{/* BME MTMT Statistics - tall 5 cols, 2 rows */}
						<motion.div
							key={updatedProjects[5].id}
							className="col-span-4 md:col-span-5 md:row-span-2 min-h-[250px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 6 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[5]}
								uniqueKey="5"
								position="left"
							/>
						</motion.div>

						{/* CEngine - stacked top - 7 cols */}
						<motion.div
							key={updatedProjects[7].id}
							className="col-span-2 md:col-span-7"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 7 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[7]}
								uniqueKey="7"
								position="right"
							/>
						</motion.div>

						{/* ProjectStructure - stacked bottom left - 4 cols */}
						<motion.div
							key={updatedProjects[9].id}
							className="col-span-2 md:col-span-4"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 8 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[9]}
								uniqueKey="9"
								position="middle"
							/>
						</motion.div>

						{/* socket-api2 - stacked bottom right - 3 cols */}
						<motion.div
							key={updatedProjects[10].id}
							className="col-span-2 md:col-span-3"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 9 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[10]}
								uniqueKey="10"
								position="right"
							/>
						</motion.div>

						{/* Project Cleaner - 6 cols */}
						<motion.div
							key={updatedProjects[8].id}
							className="col-span-4 md:col-span-6 min-h-[200px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 10 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[8]}
								uniqueKey="8"
								position="left"
							/>
						</motion.div>

						{/* Magyar Szív - 6 cols */}
						<motion.div
							key={updatedProjects[11].id}
							className="col-span-4 md:col-span-6 min-h-[200px] md:min-h-0"
							initial={{ opacity: 0, y: 24 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{
								duration: STAGGER_DURATION,
								delay: 11 * STAGGER_DELAY,
								ease: [0.22, 1, 0.36, 1]
							}}
						>
							<ProjectCard
								project={updatedProjects[11]}
								uniqueKey="11"
								position="right"
							/>
						</motion.div>
				</div>
			</div>

			<div className="section-container">
				<motion.div
					className="text-center mt-16"
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.6, delay: 0.5 }}
				>
					<motion.a
						href="https://github.com/Da4ndo?tab=repositories"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-medium"
						whileHover={{ gap: '12px' }}
						transition={{ duration: 0.3 }}
					>
						View All Projects on GitHub
						<motion.svg
							className="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							animate={{ x: [0, 4, 0] }}
							transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
						</motion.svg>
					</motion.a>
				</motion.div>
			</div>
		</section>
	)
}

export default Work
